// Argus — WallboardPage: grid live thumbnails toate stațiile (read-only spectacular pentru ops/TV).
// Bloc cleanup-batch-stream-dashboard 2026-05-09.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { fetchAgents, type Agent } from '../lib/api';
import { dayjs } from '../lib/dayjsSetup';

const REFRESH_OPTIONS = [
  { value: 5, label: '5s' },
  { value: 10, label: '10s' },
  { value: 30, label: '30s' },
  { value: 0, label: 'manual' },
] as const;

interface AgentCardProps {
  agent: Agent;
  refreshKey: number;
}

function AgentCard({ agent, refreshKey }: AgentCardProps): JSX.Element {
  const thumbnailUrl = `/api/v1/agents/${agent.id}/thumbnail?v=${refreshKey}`;
  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio: '16 / 10',
        bgcolor: 'background.paper',
        borderRadius: 1,
        overflow: 'hidden',
        boxShadow: 1,
        border: 1,
        borderColor: 'divider',
        opacity: agent.isOnline ? 1 : 0.55,
        transition: 'opacity 0.3s, transform 0.2s',
        '&:hover': { transform: 'scale(1.02)' },
      }}
    >
      <Box
        component="img"
        src={thumbnailUrl}
        alt={agent.hostname}
        loading="lazy"
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          bgcolor: 'action.hover',
        }}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          p: 1,
          bgcolor: 'rgba(0,0,0,0.65)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <FiberManualRecordIcon
            fontSize="small"
            sx={{ color: agent.isOnline ? 'success.light' : 'error.light' }}
          />
          <Typography
            variant="body2"
            noWrap
            title={agent.hostname}
            sx={{ fontWeight: 600 }}
          >
            {agent.hostname}
          </Typography>
        </Box>
        {agent.agentVersion && (
          <Chip
            label={agent.agentVersion}
            size="small"
            sx={{ height: 18, fontSize: '0.68rem', bgcolor: 'rgba(255,255,255,0.18)', color: '#fff' }}
          />
        )}
      </Box>
    </Box>
  );
}

export function WallboardPage(): JSX.Element {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshSeconds, setRefreshSeconds] = useState<number>(10);
  const [refreshKey, setRefreshKey] = useState(() => Date.now());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const idleTimer = useRef<number | null>(null);

  const loadAgents = useCallback(async (): Promise<void> => {
    try {
      const list = await fetchAgents();
      setAgents(list);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agents.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAgents();
    const id = setInterval(() => { void loadAgents(); }, 30000);
    return () => clearInterval(id);
  }, [loadAgents]);

  useEffect(() => {
    if (refreshSeconds === 0) return;
    const id = setInterval(() => setRefreshKey(Date.now()), refreshSeconds * 1000);
    return () => clearInterval(id);
  }, [refreshSeconds]);

  // Fullscreen state listener.
  useEffect(() => {
    const handler = (): void => setIsFullscreen(document.fullscreenElement !== null);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Auto-hide header in fullscreen after 3s idle.
  useEffect(() => {
    if (!isFullscreen) {
      setHeaderHidden(false);
      return;
    }
    const onMove = (): void => {
      setHeaderHidden(false);
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
      idleTimer.current = window.setTimeout(() => setHeaderHidden(true), 3000);
    };
    onMove();
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
    };
  }, [isFullscreen]);

  const sortedAgents = useMemo(() => {
    return [...agents].sort((a, b) => {
      if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
      return a.hostname.localeCompare(b.hostname);
    });
  }, [agents]);

  const toggleFullscreen = (): void => {
    if (!document.fullscreenElement) {
      void containerRef.current?.requestFullscreen();
    } else {
      void document.exitFullscreen();
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: 1,
          borderColor: 'divider',
          transition: 'opacity 0.4s, transform 0.4s',
          ...(headerHidden ? { opacity: 0, transform: 'translateY(-100%)', pointerEvents: 'none' } : {}),
        }}
      >
        <Typography variant="h6" sx={{ flexGrow: 0 }}>Wallboard</Typography>
        <Chip label={`${agents.filter((a) => a.isOnline).length} online`} color="success" size="small" />
        <Chip label={`${agents.filter((a) => !a.isOnline).length} offline`} color="error" size="small" />
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" color="text.secondary">
          Updated {dayjs(refreshKey).fromNow()}
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={refreshSeconds}
          onChange={(_, val) => { if (val !== null) setRefreshSeconds(val); }}
        >
          {REFRESH_OPTIONS.map((opt) => (
            <ToggleButton key={opt.value} value={opt.value}>{opt.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => setRefreshKey(Date.now())}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={isFullscreen ? 'Ieșire fullscreen' : 'Fullscreen'}>
          <IconButton size="small" onClick={toggleFullscreen}>
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ borderRadius: 0 }}>{error}</Alert>}

      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {loading && agents.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : sortedAgents.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary">Niciun agent vizibil.</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: 2,
            }}
          >
            {sortedAgents.map((agent) => (
              <AgentCard key={agent.id} agent={agent} refreshKey={refreshKey} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
